import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { richTextListStyles } from "../../common/richTextListStyles"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root — vertical column, padding top/bottom only                      */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px 0;

  /* EGDE-453: selection styling moved to the global stylesheet — orange
     highlight with BLACK text (the white text here was rejected by QA). */

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
    gap: ${sizes.spacing[48]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Two-column layout: content + sidebar (desktop only)                  */
/* On desktop: horizontal row with 64px gap                             */
/* RTL: source order [Sidebar, Content] renders visually as             */
/*   [Content LEFT, Sidebar RIGHT] — which matches Figma design         */
/*   where sidebar is on the LEFT (inline-end in RTL)                   */
/* ------------------------------------------------------------------ */
export const TwoColumnLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    gap: ${sizes.spacing[64]}px;
    align-items: flex-start;
  }
`

/* ------------------------------------------------------------------ */
/* Content column — 800px max on desktop, full width on mobile          */
/* ------------------------------------------------------------------ */
export const ContentColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;

  @media (min-width: ${breakpoints.lg}px) {
    width: 800px;
    flex-shrink: 0;
    gap: ${sizes.spacing[48]}px;
  }
`

/* ------------------------------------------------------------------ */
/* Sidebar — 320px, desktop only, sticky with left border               */
/* ------------------------------------------------------------------ */
export const SidebarColumn = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    flex-direction: column;
    width: 320px;
    flex-shrink: 0;
    position: sticky;
    top: ${sizes.spacing[24]}px;
    border-left: 1px solid ${colors.primitives.neutral300};
  }
`

/* ------------------------------------------------------------------ */
/* Mobile table-of-contents — EGDE-392 / EGDE-393: a collapsible anchor  */
/* dropdown (was a flat, non-sticky card that also dropped the top-level */
/* title). EGDE-393: pinned to the BOTTOM of the viewport while the      */
/* article is in view — a top-sticky bar collided with the collapsing    */
/* header while scrolling. The panel opens upward. Slides out of view    */
/* when the article scrolls off. Hidden at desktop (SidebarColumn rail   */
/* takes over).                                                          */
/* ------------------------------------------------------------------ */

// Figma "Grey/Gray M" divider (#d1d5db) — no theme primitive matches it.
const TOC_BORDER = "#d1d5db"

export const MobileTocRoot = styled.div<{ $visible?: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: ${colors.primitives.neutral0};
  box-shadow: 0px -4px 20px rgba(38, 48, 61, 0.15);
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
  transform: translateY(${(p) => (p.$visible ? "0" : "100%")});
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* Collapsed bar / toggle — shows the active section, opens the panel. */
export const MobileTocBar = styled.button`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  height: 54px;
  padding-inline: ${sizes.spacing[16]}px;
  background: ${colors.primitives.neutral0};
  border: none;
  border-block: 1px solid ${TOC_BORDER};
  cursor: pointer;
  text-align: start;
`

export const MobileTocBarText = styled(Typography)`
  flex: 1;
  min-width: 0;
  text-align: start;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700 !important;
  color: ${colors.primitives.neutral600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/* The caret at the inline-end of the bar. Bar sits at the bottom and the panel
   opens upward, so collapsed points up ("open"), expanded points down ("close").
   ArrowDown points down, so rotate 180 when closed. */
export const MobileTocCaret = styled.span<{ $open?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primitives.neutral600};
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? "0deg" : "180deg")});
`

/* The dropdown list opens upward from the bottom bar. Absolute (not in flow) so
   opening it never reflows the article — a reflow-on-close would shift a
   just-scrolled-to section out of place. */
export const MobileTocPanel = styled.div`
  position: absolute;
  bottom: 100%;
  inset-inline: 0;
  display: flex;
  flex-direction: column;
  background: ${colors.primitives.neutral0};
  box-shadow: 0px -8px 20px rgba(38, 48, 61, 0.15);
`

export const MobileTocRow = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  height: 54px;
  padding-inline: ${sizes.spacing[16]}px;
  border-bottom: 1px solid ${TOC_BORDER};
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const MobileTocRowText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: start;
  font-size: 18px;
  line-height: 22px;
  font-weight: ${(p) => (p.$active ? 700 : 500)} !important;
  color: ${colors.primitives.neutral600};
`

/* ------------------------------------------------------------------ */
/* Sidebar anchor link item (desktop rail)                              */
/* ------------------------------------------------------------------ */
export const AnchorItem = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  padding: ${sizes.spacing[16]}px;
  padding-inline-end: 0;
  text-decoration: none;
  cursor: pointer;
  width: 100%;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

export const AnchorText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  color: ${(p) =>
    p.$active ? colors.primitives.primary500 : colors.primitives.neutral600};
  font-weight: ${(p) => (p.$active ? 700 : 500)} !important;
  flex: 1;
`

export const AnchorChevron = styled.div`
  width: 12px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* Text container — categories + title + subtitle + meta bar            */
/* ------------------------------------------------------------------ */
export const TextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
`

/* ------------------------------------------------------------------ */
/* Categories row                                                        */
/* ------------------------------------------------------------------ */
export const CategoriesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${sizes.spacing[8]}px;
`

export const CategoryChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${sizes.spacing[4]}px ${sizes.spacing[12]}px;
  border-radius: ${sizes.borderRadius[16]}px;
  background-color: ${colors.primitives.neutral200};
  color: ${colors.primitives.neutral600};
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
`

/* ------------------------------------------------------------------ */
/* Title                                                                 */
/* ------------------------------------------------------------------ */
export const ArticleTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Subtitle                                                              */
/* ------------------------------------------------------------------ */
export const Subtitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Meta bar — location row, then date + social row                      */
/* On desktop: single row [location | date reading-time | social]       */
/* On mobile: location row, then date + social row                      */
/* ------------------------------------------------------------------ */
export const MetaBar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    /* EGDE-587: the location group carries the divider on its inline-end edge
       with 16px of inner padding. Without a matching gap after it the divider
       sat flush against the first glyph of the date ("עודכן"), so the rule and
       the text read as overlapping. Mirror the 16px so the divider is centred
       in its own gutter, like the date/reading-time pipe below. */
    gap: ${sizes.spacing[16]}px;
  }
`

/* Row that holds date/reading-time on one side and social icons on the other */
export const DateSocialRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex: 1;
  }
`

export const DateLocationGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.spacing[8]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[16]}px;
  }
`

/* EGDE-103: pipe divider between the date and reading time. Shown on all
   viewports (previously desktop-only and never rendered). */
export const MetaDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  background-color: ${colors.primitives.neutral400};
`

export const DateText = styled.span`
  color: ${colors.primitives.neutral600};
  font-size: 18px;
  line-height: 1.4;
  white-space: nowrap;
`

export const ReadingTimeText = styled.span`
  color: ${colors.primitives.neutral600};
  font-size: 18px;
  line-height: 1.4;
  white-space: nowrap;
`

export const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[4]}px;
  padding-inline-end: 0;
  border-inline-end: none;

  @media (min-width: ${breakpoints.lg}px) {
    /* EGDE-587: logical properties — physical padding-right/border-right rely on
       stylis-plugin-rtl flipping them, which is exactly the fragile path the
       project conventions forbid. */
    padding-inline-end: ${sizes.spacing[16]}px;
    border-inline-end: 1px solid ${colors.primitives.neutral400};
  }
`

export const LocationLabel = styled.span`
  color: ${colors.primitives.neutral600};
  font-size: 18px;
  line-height: 1.4;
  font-weight: 700;
  white-space: nowrap;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
  }
`

export const LocationValue = styled.span`
  color: ${colors.primitives.neutral600};
  font-size: 18px;
  line-height: 1.4;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
  }
`

/* ------------------------------------------------------------------ */
/* Social share row                                                      */
/* ------------------------------------------------------------------ */
export const SocialShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

export const ShareButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`

export const CopyButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const CopiedTooltip = styled.span`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: ${colors.primitives.success500};
  white-space: nowrap;
`

/* ------------------------------------------------------------------ */
/* Hero image — 800px wide, 24px border radius                          */
/* ------------------------------------------------------------------ */
export const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 800 / 509;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;
`

/* EGDE-332: the hero used to render only the desktop image, so mobile showed the
   wrong (desktop) crop. Render both and toggle by breakpoint — the mobile image
   falls back to the desktop one when no dedicated mobile crop is authored. */
export const HeroImageDesktop = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const HeroImageMobile = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* ------------------------------------------------------------------ */
/* Body content (Rich Text) wrapper with styled child elements          */
/* ------------------------------------------------------------------ */
export const BodyContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  /* EGDE-545: the RTE emits every paragraph as a direct child, so the old
     59px flex gap opened a section-sized hole between consecutive text lines.
     Normal text rhythm comes from a small gap; section rhythm comes from the
     heading margin-top below. */
  gap: ${sizes.spacing[16]}px;
  color: ${colors.primitives.neutral600};

  /* EGDE-392: anchor targets must clear the fixed header pill + the sticky
     mobile TOC bar so a clicked section doesn't land hidden underneath them. */
  & h1,
  & h2,
  & h3 {
    scroll-margin-top: 140px;
  }

  & h1 {
    font-size: 36px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
  }

  & h2 {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
  }

  & h3 {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
  }

  & h4 {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
    margin: 0;
  }

  /* EGDE-545: extra breathing room before a new section heading (adds to the
     column gap). The sibling combinator skips a heading that opens the body
     — must come after the per-heading margin:0 resets above. */
  & * + h1,
  & * + h2,
  & * + h3,
  & * + h4 {
    margin-top: ${sizes.spacing[24]}px;
  }

  & p {
    font-size: 20px;
    line-height: 1.4;
    margin: 0;
  }

  & img {
    width: 100%;
    height: auto;
    border-radius: ${sizes.borderRadius[24]}px;
    display: block;
  }

  /* EGDE-336: YouTube/embeds pasted into the article body now render
     responsively (full width, 16/9, rounded) like the dedicated video block,
     instead of honouring their intrinsic width/height. The RTE can emit either
     width/height attributes or an inline style width/height; the latter beats a
     plain rule, so force width/height with !important and cap at 100% so the
     iframe can never overflow the mobile viewport. Any wrapping element
     (figure / RTE embed preview) is neutralised the same way. */
  & iframe,
  & figure iframe,
  & div iframe {
    /* EGDE-336 (design decision 2026-07-08): mobile = end-to-end full bleed,
       square corners; desktop = article grid width, 40px rounded corners. */
    width: 100vw !important;
    max-width: 100vw;
    margin-inline: calc(50% - 50vw);
    aspect-ratio: 16 / 9;
    height: auto !important;
    border: 0;
    border-radius: 0;
    display: block;

    @media (min-width: ${breakpoints.lg}px) {
      width: 100% !important;
      max-width: 100%;
      margin-inline: 0;
      border-radius: ${sizes.borderRadius[40]}px;
    }
  }

  & figure,
  & figure > * {
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  & a {
    /* EGDE-327: rich-text body hyperlinks render blue, not orange/green */
    /* !important overrides inline color:#... pasted into CMS rich text */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-330 (round 2): QA rejected the plain orange discs — the design marks
     list items with the small brand chevron. Shared with every other
     rich-text renderer via richTextListStyles. */
  ${richTextListStyles}

  & li {
    font-size: 20px;
    line-height: 1.4;
  }

  & blockquote {
    border-inline-start: 3px solid ${colors.primitives.primary500};
    padding-inline-start: ${sizes.spacing[16]}px;
    margin: 0;
    font-style: italic;
  }

  @media (min-width: ${breakpoints.lg}px) {
    & h1 {
      font-size: 40px;
    }

    & h2 {
      font-size: 36px;
    }

    & h3 {
      font-size: 32px;
    }
  }
`

/* ------------------------------------------------------------------ */
/* YouTube video embed                                                   */
/* ------------------------------------------------------------------ */
export const VideoWrapper = styled.div`
  /* EGDE-336 (design decision 2026-07-08): mobile = end-to-end full-bleed
     strip with square corners (breaks out of the content padding); desktop =
     stays within the article grid with 40px rounded corners. */
  position: relative;
  width: 100vw;
  max-width: 100vw;
  margin-inline: calc(50% - 50vw);
  aspect-ratio: 16 / 9;
  border-radius: 0;
  overflow: hidden;
  background-color: ${colors.primitives.neutral200};

  @media (min-width: ${breakpoints.lg}px) {
    width: 100%;
    max-width: 100%;
    margin-inline: 0;
    border-radius: ${sizes.borderRadius[40]}px;
  }

  & iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: inherit;
  }
`

/* ------------------------------------------------------------------ */
/* Related articles section                                              */
/* ------------------------------------------------------------------ */
export const RelatedSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  padding-top: ${sizes.spacing[40]}px;
  border-top: 1px solid ${colors.primitives.neutral300};

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[32]}px;
    padding-top: ${sizes.spacing[48]}px;
  }
`

export const RelatedTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

export const RelatedCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding: ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius[24]}px;
  background-color: ${colors.light.bg.primary};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 150ms ease-in-out;

  &:hover {
    border-color: ${colors.primitives.neutral500};
  }

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[24]}px;
  }
`

export const RelatedCardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 352 / 220;
  border-radius: ${sizes.borderRadius[24]}px;
  overflow: hidden;
`

export const RelatedCategoryBadge = styled.div`
  position: absolute;
  /* @noflip */
  right: ${sizes.spacing[16]}px;
  bottom: ${sizes.spacing[16]}px;
  background-color: rgba(255, 255, 255, 0.65);
  border-radius: ${sizes.borderRadius.full}px;
  padding: ${sizes.spacing[6]}px ${sizes.spacing[12]}px;
  backdrop-filter: blur(4px);
`

export const RelatedBadgeText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
`

export const RelatedCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  flex: 1;
`

export const RelatedCardHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

export const RelatedCardTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
`

export const RelatedCardExcerpt = styled(Typography)`
  color: ${colors.light.text.primary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const RelatedCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

export const RelatedCtaLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
`

export const RelatedCtaText = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  white-space: nowrap;
`

export const RelatedCtaArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.primitives.primary500};

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const RelatedDateText = styled(Typography)`
  color: ${colors.light.text.primary};
  white-space: nowrap;
`
